import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettingsStore } from '../stores/settingsStore';
import { useAuthStore } from '../stores/authStore';
import { ReminderSettings } from '../components/ReminderSettings';
import { Toast } from '../components/Toast';

export function SettingsPage() {
  const { aiProvider, aiApiKey, aiModel, searchProvider, aiProviders, searchProviders, isLoading, error, fetchSettings, fetchProviders, saveAIProvider, saveSearchProvider, setAuthToken, setAIProvider } = useSettingsStore();
  const { token, setToken } = useAuthStore();
  const [localToken, setLocalToken] = useState(token || '');
  const [localAiApiKey, setLocalAiApiKey] = useState(aiApiKey);
  const [localAiModel, setLocalAiModel] = useState(aiModel);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token, fetchSettings]);

  const handleSaveAuth = async () => {
    if (!localToken.trim()) return;
    try {
      await setAuthToken(localToken.trim());
      setToken(localToken.trim());
      setTestResult('Token salvo com sucesso!');
    } catch (e) {
      setTestResult('Erro: ' + (e as Error).message);
    }
  };

  const handleSaveAI = async () => {
    try {
      await saveAIProvider(aiProvider, localAiApiKey || undefined, localAiModel || undefined);
      setTestResult('Configurações de IA salvas!');
    } catch (e) {
      setTestResult('Erro: ' + (e as Error).message);
    }
  };

  const handleSaveSearch = async () => {
    try {
      await saveSearchProvider(searchProvider);
      setTestResult('Provedor de busca salvo!');
    } catch (e) {
      setTestResult('Erro: ' + (e as Error).message);
    }
  };

  const getProviderLabel = (value: string) => {
    switch (value) {
      case 'mock': return 'Mock (Local - Gratuito, sem API key)';
      case 'openrouter': return 'OpenRouter (gratuito com API key)';
      case 'cloudflare-ai': return 'Cloudflare Workers AI (binding nativa, sem credenciais)';
      default: return value;
    }
  };

  return (
    <div style={{ display: 'block', height: '100%', minHeight: 0, overflowY: 'auto', padding: '1rem', maxWidth: '600px', margin: '0 auto' }}>
      {error && <Toast message={error} onClose={() => {}} />}
      {testResult && <Toast message={testResult} onClose={() => setTestResult(null)} />}

      {token && <Link to="/" className="btn btn-ghost" style={{ alignSelf: 'flex-start', marginBottom: '0.75rem', minHeight: '44px' }}>← Voltar ao chat</Link>}

      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>Configurações</h1>

      <ReminderSettings />

      <section className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>🔐 Autenticação</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--fg-muted)', marginBottom: '1rem' }}>
          Token único para acesso ao app. Mantenha em segredo.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <input
            type="password"
            placeholder="Digite seu token"
            value={localToken}
            onChange={e => setLocalToken(e.target.value)}
            style={{ fontFamily: 'monospace' }}
          />
          <button className="btn btn-primary" onClick={handleSaveAuth} disabled={!localToken.trim() || isLoading}>
            {isLoading ? 'Salvando...' : 'Salvar Token'}
          </button>
        </div>
      </section>

      <section className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>🤖 Provedor de IA</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Provedor</label>
            <select value={aiProvider} onChange={e => { setAIProvider(e.target.value); setLocalAiModel(''); setLocalAiApiKey(''); }}>
              {aiProviders.map(p => <option key={p} value={p}>{getProviderLabel(p)}</option>)}
            </select>
            <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)', marginTop: '0.25rem' }}>
              <strong>mock</strong>: funciona offline, custo zero, respostas simuladas.<br/>
              <strong>openrouter</strong>: requer API key (modelos gratuitos disponíveis).<br/>
              <strong>cloudflare-ai</strong>: binding nativa Workers AI (apenas no deploy Cloudflare, sem credenciais).
            </p>
          </div>
          {aiProvider === 'openrouter' && (
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>API Key (OpenRouter)</label>
              <input
                type="password"
                placeholder="sk-or-v1-..."
                value={localAiApiKey}
                onChange={e => setLocalAiApiKey(e.target.value)}
                style={{ fontFamily: 'monospace' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)', marginTop: '0.25rem' }}>
                Obtenha em <a href="https://openrouter.ai/keys" target="_blank" rel="noopener">openrouter.ai/keys</a> (modelos gratuitos disponíveis).
              </p>
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Modelo (opcional)</label>
            <input
              placeholder={aiProvider === 'mock' ? 'mock-echo' : aiProvider === 'openrouter' ? 'openrouter/auto' : '@cf/qwen/qwen3-30b-a3b-fp8'}
              value={localAiModel}
              onChange={e => setLocalAiModel(e.target.value)}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)', marginTop: '0.25rem' }}>
              Deixe vazio para usar o padrão do provedor.
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleSaveAI} disabled={isLoading}>
            {isLoading ? 'Salvando...' : 'Salvar Configurações de IA'}
          </button>
        </div>
      </section>

      <section className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>🔍 Provedor de Busca Web</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.375rem', fontWeight: 500 }}>Provedor</label>
            <select value={searchProvider} onChange={() => {}}>
              {searchProviders.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)', marginTop: '0.25rem' }}>Sem custo, sem chave API. Resultados básicos.</p>
          </div>
          <button className="btn btn-primary" onClick={handleSaveSearch} disabled={isLoading}>
            {isLoading ? 'Salvando...' : 'Salvar Provedor de Busca'}
          </button>
        </div>
      </section>

      <section className="card">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>ℹ️ Sobre</h2>
        <div style={{ fontSize: '0.875rem', color: 'var(--fg-muted)', lineHeight: 1.7 }}>
          <p><strong>Agente Gerencial</strong> v0.1.0 (MVP)</p>
          <p>Copiloto gerencial para hotelaria — mobile-first PWA.</p>
          <p>Stack: React + TypeScript + Vite, Cloudflare Workers + D1.</p>
          <p style={{ marginTop: '1rem' }}>Desenvolvido no Android com Termux + OpenCode.</p>
        </div>
      </section>
    </div>
  );
}
